import { api as generatedApi } from './generated';

const LIVE_STREAM_TAG = 'LiveStream';
const USERS_TAG = 'Users';
const TAGS_TAG = 'Tags';
const STORIES_TAG = 'Stories';
const ENGAGEMENT_STATS_API_KEY_TTL = 86400;

export const SUGGESTED_TAGS_LIST = 'SuggestedTags';

const createLiveStreamTag = (newsroom: string, assetId: number) =>
    ({ type: LIVE_STREAM_TAG, id: `${newsroom}:${assetId}` }) as const;

const createUsersTag = (id: string) => ({ type: USERS_TAG, id }) as const;

export function createTagsTag(id: string) {
    return { type: TAGS_TAG, id } as const;
}

function createStoryTag(id: string) {
    return { type: STORIES_TAG, id } as const;
}

export const api = generatedApi.enhanceEndpoints({
    addTagTypes: [LIVE_STREAM_TAG, USERS_TAG, STORIES_TAG, TAGS_TAG],
    endpoints: {
        LiveStream: {
            providesTags(result, _, { newsroom, assetId }) {
                return result?.live ? [createLiveStreamTag(newsroom, assetId)] : [];
            },
        },
        PrepareLiveStream: {
            invalidatesTags(result, _, { newsroom, assetId }) {
                return result ? [createLiveStreamTag(newsroom, assetId)] : [];
            },
        },
        Users: {
            providesTags(result, _, { query }) {
                const data = result?.users || [];
                return [createUsersTag(query), ...data.map(({ email }) => createUsersTag(email))];
            },
        },
        UpdateUser: {
            invalidatesTags(result, _, { email }) {
                return result?.updateUser ? [createUsersTag(email)] : [];
            },
        },
        CreateUser: {
            invalidatesTags(result) {
                return result?.createUser ? [USERS_TAG] : [];
            },
        },
        ModifyAssetPodmeMetadata: {
            async onQueryStarted({ provider, assetId }, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                dispatch(
                    api.util.updateQueryData('AssetPodmeMetadata', { provider, assetId }, (draft) => {
                        Object.assign(draft, { assetPodmeMetadata: data.modifyAssetPodmeMetadata });
                    })
                );
            },
        },
        ModifyCategoryPodmeMetadata: {
            async onQueryStarted({ provider, categoryId }, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                dispatch(
                    api.util.updateQueryData('CategoryPodmeMetadata', { provider, categoryId }, (draft) => {
                        Object.assign(draft, { categoryPodmeMetadata: data.modifyCategoryPodmeMetadata });
                    })
                );
            },
        },
        ListTags: {
            providesTags(result) {
                if (!result) {
                    return [];
                }
                return result.listTags.map(({ id }) => createTagsTag(id));
            },
        },
        SearchTags: {
            providesTags(result) {
                if (!result) {
                    return [];
                }
                return result.searchTags.map(({ id }) => createTagsTag(id));
            },
        },
        SuggestTags: {
            providesTags(result) {
                if (!result) {
                    return [createTagsTag(SUGGESTED_TAGS_LIST)];
                }
                return [...result.suggestTags.map(({ id }) => createTagsTag(id)), createTagsTag(SUGGESTED_TAGS_LIST)];
            },
        },
        Stories: {
            providesTags(result) {
                if (!result) {
                    return [];
                }
                return result.stories.map(({ id }) => createStoryTag(id));
            },
        },
        SearchStories: {
            providesTags(result) {
                if (!result) {
                    return [];
                }
                return result.searchStories.map(({ id }) => createStoryTag(id));
            },
        },
        EngagementStatsApiKey: {
            keepUnusedDataFor: ENGAGEMENT_STATS_API_KEY_TTL,
        },
    },
});

export const adminBffSdk = api;

export const {
    useStoriesQuery,
    useListTagsQuery,
    useSearchStoriesQuery,
    useSearchTagsQuery,
    useSuggestTagsQuery,
    useEngagementStatsApiKeyQuery,
    useLiveArchiveExpirationQuery,
    useLiveArchiveQuery,
} = api;
